import { PACKAGING_ITEMS_RATING } from '@app/ccf/company-report/utils';
import ReportContent from '@app/components/reports/components/report-content';
import ReportMetric from '@app/components/reports/components/report-metric';
import { ReportSectionComponent } from '@app/components/reports/types';
import {
  useGetReportSectionData,
  useGetReportTypeCopy,
} from '@app/components/reports/utils';
import {
  ContextMetricEnum,
  ContextMetricUnitEnum,
  EmissionsCategoryEnum,
  OperationReportTypeEnum,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import Unit from '@shared/components/content/unit';
import NewRatingsThresholdSlider from '@shared/components/new-ratings-threshold-slider';
import RatingCloud from '@shared/components/ratings/rating-cloud';
import { Card, CardContent } from '@shared/components/ui/card';
import {
  cn,
  formatNumber,
  formatSmallNumber,
  formatUnderscoreText,
} from '@shared/lib/utils';

const currencyUnitMap: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
};

// Copy for context metric: Emissions per [(£/$/€) (spent/earned)] or person served

function getMetricCopy(
  contextMetric: ContextMetricEnum,
  currency: ContextMetricUnitEnum
) {
  switch (contextMetric) {
    case ContextMetricEnum.money_earned:
      return {
        short: currencyUnitMap[currency],
        long: `${currencyUnitMap[currency]} earned`,
        basedOn: 'amount of money you earned',
      };
    case ContextMetricEnum.money_spent:
      return {
        short: currencyUnitMap[currency],
        long: `${currencyUnitMap[currency]} spent`,
        basedOn: 'amount of money you spent',
      };
    case ContextMetricEnum.people_served:
      return {
        short: 'person',
        long: 'person served',
        basedOn: 'number of people you served',
      };
    default:
      return {
        short: '',
        long: '',
        basedOn: '',
      };
  }
}

interface ResultsOverviewProps extends ReportSectionComponent {}

export default function ResultsOverview({
  reportType,
  downloadMode,
}: ResultsOverviewProps) {
  const {
    result,
    result: { foodAndDrink, packaging },
  } = useGetReportSectionData(reportType);
  const reportTypeCopy = useGetReportTypeCopy(reportType);

  const data = {
    totalEmissionsTonnes: result.totalEmissionsTonnes,
    contextMetric: result.contextMetric,
    contextMetricEmissionsKilograms: result.contextMetricEmissionsKilograms,
    contextMetricValue: result.contextMetricValue,
    contextMetricUnit: result.contextMetricUnit,
    carbon_intensity_by_category: [
      ...(foodAndDrink
        ? [
            {
              category: 'Food and drink',
              weight: (foodAndDrink?.totalWeightKg ?? 0) / 1000,
              emissions: foodAndDrink?.totalEmissionsTonnes,
              carbon_intensity: foodAndDrink?.averageCarbonIntensity,
              rating: foodAndDrink?.carbonIntensityRating as
                | EmissionsCategoryEnum
                | '',
            },
          ]
        : []),
      ...(packaging
        ? [
            {
              category: 'Packaging',
              weight: (packaging?.totalWeightKg ?? 0) / 1000,
              emissions: packaging?.totalEmissionsTonnes,
              carbon_intensity: packaging?.averageCarbonIntensity,
              rating:
                packaging?.carbonIntensityRating as keyof typeof PACKAGING_ITEMS_RATING,
            },
          ]
        : []),
    ],
  };

  const metricCopy = getMetricCopy(
    data.contextMetric as ContextMetricEnum,
    data.contextMetricUnit as ContextMetricUnitEnum
  );
  return (
    <ReportContent variant="column">
      <>
        <Text variant="reportBody">
          This {OperationReportTypeEnum[reportType]} report calculates emissions
          based on the quantity of each item {reportTypeCopy.past}, with each{' '}
          {reportTypeCopy.singular} matched to an item in the My Emissions
          database.
          <br />
          <br />
          Emissions values are reported in tonnes of carbon dioxide equivalent
          (tCO₂e), a standard unit used to measure the impact of all greenhouse
          gas emissions, expressed in terms of the equivalent amount of CO₂.
        </Text>
        <ReportMetric
          title="Total emissions"
          value={data.totalEmissionsTonnes}
          unit="tCO2e"
          size="lg"
          className="my-8"
        />
        {data.contextMetric && (
          <div className="space-y-3">
            <Text variant="h4" className="text-lg font-bold text-primary-600">
              Emissions per {metricCopy.long}
            </Text>
            <Text variant="reportBody">
              We have calculated a custom intensity metric based on the{' '}
              {metricCopy.basedOn}, provided when creating the report.
            </Text>
            <div className="flex gap-x-3">
              <ReportMetric
                title={formatUnderscoreText(data.contextMetric || '')}
                value={data.contextMetricValue || 0}
                unit={
                  currencyUnitMap[data.contextMetricUnit || ''] ??
                  data.contextMetricUnit
                }
                size="md"
                className="flex-1"
                unitClassName={cn('text-4xl text-primary-800 font-medium', {
                  'order-first': data.contextMetricUnit,
                })}
              />
              <ReportMetric
                title={`Emissions per ${metricCopy.short}`}
                value={data.contextMetricEmissionsKilograms!}
                unit="kgCO2e"
                size="md"
                className="flex-1"
              />
            </div>
          </div>
        )}
      </>
      <>
        <Text variant="h3">Carbon intensity</Text>
        <Text variant="reportBody">
          Carbon intensity is a measure of the emissions produced per kilogram
          of item {reportTypeCopy.past}. This provides a standardised way of
          comparing the impact of different items, as well as normalising your
          total emissions by the total weight of items {reportTypeCopy.past}.
        </Text>

        <div className="space-y-3">
          <div className="grid grid-cols-12 gap-x-4">
            <Text className="col-span-3">Category</Text>
            <span className="col-span-3 text-right">
              <Text>Weight</Text> <Unit variant="tonnes" />
            </span>
            <span className="col-span-3 text-right">
              <Text>Emissions</Text>
              <Unit variant="tCO2e" />
            </span>
            <span className="col-span-3 text-right">
              <Text>Carbon intensity</Text>
              <Unit variant="kgCO2e/kg" />
            </span>
          </div>
          {data.carbon_intensity_by_category.length > 0 &&
            data.carbon_intensity_by_category?.map((item, idx) => {
              return (
                idx < 6 && (
                  <div
                    key={idx}
                    className="grid grid-cols-12 items-center gap-4"
                  >
                    <Text className="col-span-3">{item.category}</Text>
                    <Text className="col-span-3 text-right">
                      {formatNumber(item?.weight)}
                    </Text>
                    <Text className="col-span-3 text-right">
                      {formatSmallNumber(item?.emissions as number)}
                    </Text>
                    <span className="col-span-3 flex items-center justify-end gap-2 text-right">
                      {item?.category === 'Food and drink' ? (
                        <RatingCloud
                          rating={item?.rating as EmissionsCategoryEnum}
                          className="w-8 justify-self-center object-contain"
                          format={downloadMode ? 'png' : 'svg'}
                        />
                      ) : (
                        <div
                          className={cn('size-4 shrink-0 rounded-sm mt-0.5 ')}
                          style={{
                            backgroundColor:
                              item?.rating &&
                              item.rating in PACKAGING_ITEMS_RATING
                                ? PACKAGING_ITEMS_RATING[
                                    item.rating as keyof typeof PACKAGING_ITEMS_RATING
                                  ].color
                                : '#cccccc',
                          }}
                        ></div>
                      )}

                      <Text>
                        {formatSmallNumber(item?.carbon_intensity as number)}
                      </Text>
                    </span>
                  </div>
                )
              );
            })}
        </div>

        {foodAndDrink && (
          <div className="space-y-3">
            <Text variant="h3">My Emissions Carbon Rating for food</Text>
            <Text variant="reportBody">
              Our food carbon rating scale, similar to nutritional labelling,
              communicates carbon intensity in a visual way.{' '}
            </Text>
            <Card className="w-96 border-teal">
              <CardContent className="py-2 pb-4">
                <NewRatingsThresholdSlider
                  value={foodAndDrink!.averageCarbonIntensity}
                  methodologyVersion={2}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </>
    </ReportContent>
  );
}
